import React from 'react';
import { Shield, Droplets, Layers, Sun, Truck, Zap } from 'lucide-react';

// Images fournies par l'utilisateur
const DALLE_GEMINI_URL = "https://customer-assets.emergentagent.com/job_101ab4cd-4d37-431a-b507-ce3ea7ef67e4/artifacts/bpx7f0rx_Dalle%20Gemini%20%2B%20Logo%20site.png";
const ARRIERE_ECRAN_URL = "https://customer-assets.emergentagent.com/job_101ab4cd-4d37-431a-b507-ce3ea7ef67e4/artifacts/sn4estsd_Arri%C3%A8re%20%C3%A9cran%20LED%20Gemini%20-%20Phocea%20Media.png";

const features = [
  {
    icon: Droplets,
    title: "IP65 Étanche",
    description: "Résistance à la pluie et aux poussières pour une utilisation intérieure et extérieure"
  },
  {
    icon: Layers,
    title: "Assemblage modulaire",
    description: "Dalles 50x50 et 50x100 cm combinables pour créer l'écran idéal"
  },
  {
    icon: Shield,
    title: "Protection des angles",
    description: "Système de protection pour éviter d'abîmer le matériel lors des transports"
  },
  {
    icon: Sun,
    title: "Haute luminosité",
    description: "5 000 nits pour une visibilité parfaite même en plein soleil"
  },
  {
    icon: Truck,
    title: "Flight cases inclus",
    description: "Caisses de transport sur roulettes pour un rangement facile et pratique"
  },
  {
    icon: Zap,
    title: "Plug & Play",
    description: "Installation rapide avec système de verrouillage et d'accroche polyvalent"
  }
];

const specs = [
  { label: "Pitch", value: "3.9" },
  { label: "Usage", value: "Extérieur et intérieur" },
  { label: "Luminosité", value: "5 000 nits" },
  { label: "Consommation moyenne", value: "400 W/m²" },
  { label: "Poids dalle 50x50", value: "8 kg" },
  { label: "Durée de vie", value: "100 000 heures" }
];

const ProductPresentationSection = () => {
  return (
    <section id="product" className="py-24 bg-black text-white overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="inline-block text-[#DFFF00] font-semibold text-sm uppercase tracking-wider mb-4">
            Technologie GEMINI
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
            Présentation du produit
          </h2>
          <p className="text-lg text-slate-400 max-w-3xl mx-auto">
            L'écran LED le plus polyvalent jamais conçu. Une structure IP65 étanche et robuste 
            avec des modules interchangeables pour s'adapter à tous vos projets.
          </p>
        </div>

        {/* Main Content - Two Columns */}
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center mb-20">
          
          {/* Left - Image avec effet glow intégré */}
          <div className="relative group">
            {/* Glow effect - plus diffus et intégré */}
            <div className="absolute inset-0 bg-[#DFFF00]/30 rounded-[40px] blur-[100px] scale-90" />
            <div className="absolute inset-0 bg-[#DFFF00]/10 rounded-[60px] blur-[120px] scale-110" />
            
            {/* Image sans bordure visible */}
            <div className="relative">
              <img
                src={DALLE_GEMINI_URL}
                alt="Dalle LED Gemini - Phocea Rent"
                className="w-full h-auto object-contain rounded-2xl drop-shadow-[0_0_30px_rgba(223,255,0,0.3)]"
              />
              <div className="absolute top-4 right-4">
                <span className="bg-[#DFFF00] text-black text-xs font-bold px-3 py-1 rounded-full shadow-lg shadow-[#DFFF00]/30">
                  GAMME GEMINI
                </span>
              </div>
            </div>
          </div>

          {/* Right - Features */}
          <div>
            <h3 className="text-2xl font-bold text-white mb-6">
              Pourquoi la gamme GEMINI est différente ?
            </h3>
            <p className="text-slate-400 mb-8 leading-relaxed">
              En plus de vous proposer une structure IP65 étanche et robuste, vous pourrez 
              interchanger les modules LED directement sur les dalles pour obtenir la configuration 
              d'écran que vous souhaitez. <span className="text-[#DFFF00] font-semibold">Un écran, plusieurs configurations.</span>
            </p>
            
            <div className="grid sm:grid-cols-2 gap-4">
              {features.map((feature, index) => {
                const Icon = feature.icon;
                return (
                  <div 
                    key={index}
                    className="bg-slate-800/50 rounded-xl p-4 border border-slate-700 hover:border-[#DFFF00]/50 transition-colors group"
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 bg-[#DFFF00]/10 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-[#DFFF00]/20 transition-colors">
                        <Icon className="w-5 h-5 text-[#DFFF00]" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-white text-sm mb-1">{feature.title}</h4>
                        <p className="text-slate-400 text-xs leading-relaxed">{feature.description}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Second Row - Specs & Back View */}
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          
          {/* Left - Specifications */}
          <div className="order-2 lg:order-1">
            <h3 className="text-2xl font-bold text-white mb-6">
              Caractéristiques techniques
            </h3>
            <p className="text-slate-400 mb-8 leading-relaxed">
              Nos dalles pitch 3.9 en Black Face offrent un contraste optimal 
              et une luminosité adaptée pour une utilisation en extérieur comme en intérieur.
            </p>
            
            <div className="bg-slate-800/50 rounded-2xl border border-slate-700 overflow-hidden">
              <div className="grid grid-cols-2">
                {specs.map((spec, index) => (
                  <div 
                    key={index}
                    className={`p-4 ${index % 2 === 0 ? 'border-r border-slate-700' : ''} ${index < specs.length - 2 ? 'border-b border-slate-700' : ''}`}
                  >
                    <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">{spec.label}</p>
                    <p className="text-[#DFFF00] font-semibold">{spec.value}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right - Back View Image */}
          <div className="relative order-1 lg:order-2 group">
            {/* Glow effect - plus diffus et intégré */}
            <div className="absolute inset-0 bg-[#DFFF00]/25 rounded-[40px] blur-[100px] scale-90" />
            <div className="absolute inset-0 bg-[#DFFF00]/10 rounded-[60px] blur-[120px] scale-110" />
            
            {/* Image sans bordure visible */}
            <div className="relative">
              <img
                src={ARRIERE_ECRAN_URL}
                alt="Arrière écran LED Gemini - Système concave/convexe"
                className="w-full h-auto object-contain rounded-2xl drop-shadow-[0_0_30px_rgba(223,255,0,0.3)]"
              />
              {/* Texte explicatif sous l'image */}
              <div className="mt-6 bg-black/60 backdrop-blur-md rounded-xl p-5 border border-[#DFFF00]/20">
                <p className="text-sm text-slate-300 leading-relaxed">
                  <span className="text-[#DFFF00] font-semibold">Configuration concave / convexe</span> : 
                  Grâce au système de verrouillage à charnière, vous pouvez courber votre installation 
                  avec une précision de 2,5° sous 5 angles différents. Créez des écrans incurvés vers 
                  l'intérieur (concave) ou vers l'extérieur (convexe) pour des installations créatives 
                  et immersives.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="mt-20 text-center">
          <div className="inline-flex items-center gap-4 bg-slate-800/50 rounded-2xl p-6 border border-slate-700">
            <div className="text-left">
              <p className="text-slate-400 text-sm">Besoin de conseils personnalisés ?</p>
              <p className="text-white font-semibold">Notre équipe d'experts est là pour vous accompagner</p>
            </div>
            <a 
              href="/contact"
              className="bg-[#DFFF00] hover:bg-[#c8e600] text-black font-semibold px-6 py-3 rounded-full transition-colors whitespace-nowrap"
            >
              Nous contacter
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProductPresentationSection;
